import { useCallback, useState } from 'react';

import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  TextField,
  Tooltip,
  Typography,
  makeStyles,
} from '@material-ui/core';

import {
  useAlert,
  useAuth,
  useAuthFactory,
} from '@spotify/confidence-core-react';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--encore-spacing-base)',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--encore-spacing-tighter-2)',
  },
  overrideEnabledChip: {
    padding: 'var(--encore-spacing-tighter-4) var(--encore-spacing-tighter)',
    backgroundColor: 'var(--background-warning-subdued-base)',
    border: '1px solid var(--essential-warning)',
    borderRadius: '9999px',
  },
  card: {
    maxWidth: 450,
  },
  cardFooter: {
    justifyContent: 'flex-end',
  },
});

export const AuthDebug = () => {
  const classes = useStyles();
  const alert = useAlert();
  const auth = useAuth();
  const { overrideToken, setOverrideToken } = useAuthFactory();
  const [authToken, setAuthToken] = useState(overrideToken ?? '');

  const copyToken = useCallback(async () => {
    let token = overrideToken || auth.token;
    if (!token) {
      await auth.getAccessTokenSilently().then(fetchedToken => {
        token = fetchedToken;
      });
    }

    if (token) {
      navigator.clipboard.writeText(token);
      alert.post({
        message: 'Auth token copied to clipboard',
        severity: 'success',
        display: 'transient',
      });
    } else {
      alert.post({
        message: 'Auth token unavailable',
        severity: 'error',
        display: 'transient',
      });
    }
  }, [overrideToken, alert, auth]);

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>
          Authorization
        </Typography>

        {!!overrideToken && (
          <div className={classes.overrideEnabledChip} role="status">
            <Typography
              variant="body2"
              component="span"
              style={{ color: 'var(--essential-warning)' }}
            >
              Override enabled
            </Typography>
          </div>
        )}
      </div>
      <Card className={classes.card}>
        <CardHeader
          title="Token"
          titleTypographyProps={{
            variant: 'body1',
            style: { fontWeight: 'bold' },
          }}
          action={
            <Tooltip title="Copy the current auth token to the clipboard.">
              <Button size="small" onClick={copyToken} variant="outlined">
                Copy
              </Button>
            </Tooltip>
          }
        />
        <CardContent>
          <TextField
            id="auth-token"
            name="auth-token"
            label="Custom auth token"
            placeholder="Enter a custom auth token"
            value={authToken}
            onChange={e => setAuthToken(e.target.value)}
            autoComplete="off"
            helperText="Override the auth token for the current session. This will be used
              for all requests to Confidence services until the override is cleared."
            fullWidth
          />
        </CardContent>
        <CardActions className={classes.cardFooter}>
          <Button
            disabled={!overrideToken}
            onClick={() => setOverrideToken(undefined)}
          >
            Clear override
          </Button>
          <Button
            disabled={!authToken}
            onClick={() => setOverrideToken(authToken)}
            variant="outlined"
          >
            Override
          </Button>
        </CardActions>
      </Card>
    </div>
  );
};
